import React, { useEffect, useState } from 'react';
import { getSecureEmail } from "../service/api-actions.js";
import { useParams } from 'react-router';
import { sendEmails } from '../helper/emailHelper.js';
import { toast, Bounce } from 'react-toastify';
import { logo } from '../assets/svg/svg.js';
import { CompleteLoader } from '../modals/common-alert.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';

const SecureMail = () => {
    const { id } = useParams();
    const [email, setEmail] = useState(null);
    const [reply, setReply] = useState({ subject: '', message: '', files: [] });
    const [sending, setSending] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isExpired, setIsExpired] = useState(false);
    const [text, setText] = useState("")

    useEffect(() => {
        const fetchEmail = async () => {
            try {
                const res = await getSecureEmail(id);
                console.log(res);
                if (res.success) {
                    setEmail(res.data?.result);
                    setReply({
                        subject: `Re: ${res.data?.result?.subject}`,
                        //set the reply message to the message of the email it is a html string
                        message: `
                        
-----------------------------------------------------
${res.data?.result?.message?.replace(/<[^>]*>?/g, '')}`,
                        files: []
                    });

                } else {
                    setIsExpired(true);
                    setEmail(null);
                    setReply({
                        subject: '',
                        message: '',
                        files: []
                    });

                }
            } catch (error) {
                const userName = localStorage.getItem('UserName');
                const errorStr = toErrorString(error || error.message);
                reportError({
                    endpoint: '/getSecureEmail',
                    message: `FileName: SecureMail.js, Fun: fetchEmail, ${String(error.message || 'Unhandled error')}`,
                    error: errorStr,
                    userName: userName || '',
                });
                console.error("Error fetching email:", error);
            }
        };
        if (id) fetchEmail();
    }, [id]);

    function b64toBlob(base64, mime = '') {
        const byteChars = atob(base64);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
            byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mime });
    }

    function formatSize(base64) {
        if (!base64) return "Unknown size";
        const sizeInBytes = (base64.length * (3 / 4)) - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
        const sizeInKB = sizeInBytes / 1024;
        return `${sizeInKB.toFixed(1)} KB`;
    }

    const handleReplyChange = (e) => {
        const { name, value } = e.target;
        setReply(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        const combinedFiles = [...selectedFiles, ...newFiles];
        const maxSize = 25 * 1024 * 1024; // 25 MB in bytes
        const totalSize = combinedFiles.reduce((acc, file) => acc + file.size, 0);
        if (totalSize > maxSize) {
            toast.error("Total file size exceeds the limit of 25 MB.", {
                position: "top-center",
                transition: Bounce,
            });
            return;
        }
        setSelectedFiles(combinedFiles);
    };

    // Convert plain text to HTML with <p> blocks and <br> for empty lines
    const formatTextToParagraphs = (input) => {
        if (!input) return "";
        const lines = input.replace(/\r\n|\r/g, "\n").split("\n");
        const html = lines.map((line) => {
            if (line.trim() === "") {
                return "<p><br></p>";
            }
            const escaped = line
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/\"/g, "&quot;")
                .replace(/'/g, "&#39;");
            return `<p>${escaped}</p>`;
        }).join("");
        return html;
    };

    const handleReplySubmit = async (e) => {
        e.preventDefault();
        
        if (sending) {
            return;
        }
        
        setSending(true);
        setText("Sending your reply...")
        try {
            const formattedMessage = formatTextToParagraphs(reply?.message);
            const isSend = await sendEmails([email?.from], [''], [''], reply?.subject, formattedMessage, false, 'msg', selectedFiles, null, email.to?.split(',')?.[0]);
            if (isSend) {
                toast.success('Email sent successfully', {
                    position: "top-center",
                    transition: Bounce,
                });
                setSelectedFiles([]);
                setReply({
                    subject: `Re: ${email?.subject}`,
                    message: `
                        
-----------------------------------------------------
${email?.message?.replace(/<[^>]*>?/g, '')}`,
                    files: []
                });
            } else {
                toast.error("Failed to send reply", {
                    position: "top-center",
                    transition: Bounce,
                });
            }
        } catch (err) {
            const errorStr = toErrorString(err || err.message);
            const userName = localStorage.getItem('UserName');
                reportError({
                  endpoint: '-',
                  message: `FileName: SecureMail.js, Fun: handleReplySubmit, ${String(err.message || 'Unhandled error')}`,
                  error: errorStr,
                  userName: userName || '',
                });
            console.error("Failed to send reply:", err);
            toast.error("Failed to send reply", {
                position: "top-center",
                transition: Bounce,
            });
        } finally {
            setSending(false);
            setText("");
        }
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    };

    if (!email && !isExpired) {
        return (
            <div className="container mt-4">
                <div className="container-xl my-5">
                    <div className="d-flex flex-column gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="avatar bg-secondary-custom skeleton avatar-40"></div>
                            <div className="flex-grow-1">
                                <div className="skeleton skeleton-70"></div>
                                <div className="skeleton width-40-height-14"></div>
                            </div>
                        </div>

                        <div className="skeleton skeleton-full"></div>

                        <div className="card">
                            <div className="card-body">
                                <div className="skeleton skeleton-full-height-150"></div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-3">
                            {Array.from({ length: 2 }).map((_, index) => (
                                <div
                                    key={index}
                                    className="card card-max-250"
                                >
                                    <div className="card-body d-flex align-items-center gap-3 p-2">
                                        <div className="skeleton skeleton-24"></div>
                                        <div className="flex-grow-1">
                                            <div className="skeleton skeleton-80-12"></div>
                                            <div className="skeleton skeleton-60-10"></div>
                                        </div>
                                        <div className="skeleton skeleton-16"></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="skeleton skeleton-30-16"></div>
                                <div className="skeleton skeleton-100-40"></div>
                                <div className="skeleton skeleton-100-100"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isExpired) {
        return (
            <div className="d-flex justify-content-center align-items-center fullscreen-center">
                <div className="card shadow-lg rounded-4 p-4 card-480">
                    <div className="d-flex flex-column align-items-center gap-4">
                        <div className="position-relative overflow-hidden box-175x105">
                            {logo.expired_svg}
                        </div>
                        <div className="text-center">
                            <h5 className="fw-semibold text-dark mb-2">Your Session has Expired</h5>
                            {/* <p className="text-muted small mb-0">
                                Your session has been expired. To continue working, please log in again to your account.
                            </p> */}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <div className="container-xl my-5">
                <div className="d-flex flex-column gap-3">
                    <div className="d-flex flex-column gap-4 pb-5 border-bottom">
                        <div className="d-flex align-items-center gap-3">
                            <div className="avatar bg-secondary-custom">{email.to?.split(',')?.[0]?.[0]}</div>
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-medium">{email.to?.split(',')}</span>
                                </div>
                                <div className="text-muted small">{new Date(email.currentTime).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                })} at {new Date(email.currentTime).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                })}</div>
                                <div className="mt-2">
                                    <span className=" badge-encrypted p-1">Encrypted</span>
                                </div>
                            </div>
                        </div>
                        {/* add subject */}
                        <span className="text-bold mb-0"><b>{email.subject}</b></span>
                        <div className="card">
                            <div className="card-body">
                                <p className="text-muted mb-0" dangerouslySetInnerHTML={{ __html: email.message }} />
                            </div>
                        </div>
                        <div className="d-flex flex-wrap gap-3">
                            {email.attachments?.length > 0 &&
                                email.attachments.map((file, index) => {
                                    const blob = b64toBlob(file.contentBase64, file.mimeType);
                                    const url = URL.createObjectURL(blob);

                                    return (
                                        <div className="card card-max-250" key={index}>
                                            <div className="card-body d-flex align-items-center gap-3 p-2">
                                                <div className='flex-no-shrink'>{logo.doc_icon_secure}</div>
                                                <div className="font-inter flex-grow-1 text-truncate min-width-0">
                                                    <div className="fw-medium small text-truncate" title={file?.originalFilename}>
                                                        {file?.originalFilename}
                                                    </div>
                                                    <div className="text-muted small">{formatSize(file.contentBase64)}</div>
                                                </div>
                                                <a
                                                    href={url}
                                                    download={file.originalFilename}
                                                    className="d-flex align-items-center flex-no-shrink"
                                                >
                                                    {logo.download_svg_attachment}
                                                </a>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                        <hr />
                        <div className="row g-4">
                            <div className="col-lg">
                                <div className="d-flex gap-3">
                                    <div className="avatar bg-secondary-custom flex-shrink-0">
                                        {logo.secure_avatar}
                                    </div>

                                    <div className="d-flex flex-column gap-4 flex-grow-1">
                                        <form onSubmit={handleReplySubmit}>
                                            <div className="card">
                                                <div className="card-body d-flex flex-column gap-3">
                                                    <div className="form-like-div p-2 rounded d-flex align-items-center gap-2">
                                                        <span className='d-flex align-items-center justify-content-center'>{logo.reply_icon_secure}</span>
                                                        <span className="text-secondary small">{email.from}</span>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className="form-like-div p-2 rounded w-100"
                                                        name="subject"
                                                        value={reply.subject}
                                                        onChange={handleReplyChange}
                                                        required
                                                    />
                                                    <div className="d-flex align-items-center gap-3 text-primary">
                                                        <i className="bi bi-type-bold"></i>
                                                        <i className="bi bi-type-italic"></i>
                                                        <i className="bi bi-type-underline"></i>
                                                        <i className="bi bi-list-ol"></i>
                                                        <i className="bi bi-link-45deg"></i>
                                                    </div>
                                                    <textarea
                                                        className="form-control"
                                                        name="message"
                                                        rows="5"
                                                        value={reply.message}
                                                        onChange={handleReplyChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="card file-drop-zone d-flex p-3 gap-3 mt-2"
                                                onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                                                onDrop={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const files = Array.from(e.dataTransfer.files);
                                                    // Create a synthetic event to reuse handleFileChange
                                                    const syntheticEvent = { target: { files } };
                                                    handleFileChange(syntheticEvent);
                                                }}
                                            >
                                                <div className="card-body text-center p-4">
                                                    {logo.file_cloud}
                                                    <div>
                                                        <div className="btn btn-link p-0 text-decoration-none fw-semibold"
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                document.getElementById('secure-file-upload').click();
                                                            }}
                                                        >Click to upload</div>
                                                        <span className="text-muted">or drag and drop</span>
                                                    </div>
                                                    <input
                                                        id="secure-file-upload"
                                                        type="file"
                                                        className="d-none"
                                                        multiple
                                                        onChange={handleFileChange}
                                                    />
                                                    <div className="text-muted small">SVG, PNG or JPG (max size. 25mb)</div>
                                                    {selectedFiles.length > 0 && (
                                                        <div className="mt-2">
                                                            <hr />
                                                            <ul className="list-unstyled mb-0 d-flex flex-wrap gap-2">
                                                                {selectedFiles.map((file, idx) => (
                                                                    <li key={idx} className="d-flex align-items-center bg-light border rounded px-2 py-1 me-2 mb-1 font-095em">
                                                                        <span className="me-2">{file.name} <span className="text-muted">({(file.size / 1024).toFixed(1)} KB)</span></span>
                                                                        <button
                                                                            type="button"
                                                                            className="btn-close btn-close-sm ms-1 font-07em"
                                                                            aria-label="Remove"
                                                                            onClick={() => handleRemoveFile(idx)}
                                                                        ></button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="d-flex gap-3 justify-content-end p-2">
                                                <button type="submit" className="w-40 sucure-btn-custom" disabled={sending}>{sending ? 'Sending...' : 'Secure Send'}</button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CompleteLoader isOpen={sending} text={text} />
        </div >
    );
};

export default SecureMail;
